/**
 * @file     rentalsListing.ts
 * @readme   ../../CLAUDE.md
 * @purpose  English strings for the rental listing pages (MLS® lease variant and the owner listing page), the viewing request sheet and the admin preview. Copy from the Relta Rentals design, sections 4, 5 and 14, checked against docs/voice-and-tone.md.
 */
export const rentalsListing = {
  price: {
    perMonth: '/month',
    perMonthShort: '/mo',
    available: 'Available {{date}}',
    availableNow: 'Available now',
    rentAria: '{{rent}} a month',
  },
  badge: {
    forRent: 'For rent',
    listedByRay: 'Listed by Ray Rasouli',
  },
  forwardLine: 'Your request goes to the listing brokerage for this home.',
  ownerLine: 'Your request goes to Ray Rasouli, who represents the owner of this home.',
  rayCopyLine: 'Your request goes to Ray.',
  about: {
    title: 'About this rental',
  },
  homeType: {
    apartment_condo: 'Apartment/Condo',
    townhouse: 'Townhouse',
    house: 'House',
    suite: 'Suite',
    laneway: 'Laneway house',
    duplex: 'Duplex',
    other: 'Home',
  },
  crumbs: {
    home: 'Home',
    rentals: 'Rentals',
    listedByRay: 'Listed by Ray Rasouli',
    label: 'Breadcrumb',
  },
  specs: {
    beds: 'Beds',
    baths: 'Baths',
    sqft: 'Sqft',
    parking: 'Parking',
    label: 'Home facts',
  },
  terms: {
    title: 'Term and policies',
    leaseTerm: 'Lease term',
    available: 'Available',
    pets: 'Pets',
    furnished: 'Furnished',
    laundry: 'Laundry',
    parking: 'Parking',
    rentersInsurance: "Renter's insurance",
    shortTerm: 'Short term',
    utilities: 'Utilities included',
    rules: 'Rules',
    detailsLabel: 'Term details, as written in the listing',
    rentIncludes: 'What rent includes is not in the MLS® data. Ask about it in your viewing request.',
    ownerChecked: 'Details from the owner, checked by Ray Rasouli before the listing went live.',
    notStated: 'Not stated',
    now: 'Now',
    yes: 'Yes',
    no: 'No',
    required: 'Required',
    notRequired: 'Not required',
    none: 'None',
    parkingOne: '1 space',
    parkingMany: '{{count}} spaces',
    petsValue: {
      allowed: 'Allowed',
      cats: 'Cats',
      dogs: 'Dogs',
      no: 'No pets',
      ask: 'Ask first',
    },
    furnishedValue: {
      yes: 'Yes',
      no: 'No',
      partly: 'Partly',
    },
    laundryValue: {
      in_unit: 'In suite',
      in_building: 'Shared in building',
      none: 'None',
    },
    leaseTermValue: {
      short: 'Short term',
      month_to_month: 'Month-to-month',
      six_months: '6-month fixed term',
      one_year: '1-year fixed term',
      other: 'Other term',
    },
    utilityValue: {
      heat: 'Heat',
      hot_water: 'Hot water',
      electricity: 'Electricity',
      water: 'Water',
      gas: 'Gas',
      internet: 'Internet',
    },
  },
  moveIn: {
    title: 'Move-in cost',
    firstMonth: "First month's rent",
    security: 'Security deposit',
    pet: 'Pet deposit, with a pet',
    cap: 'BC maximum: half a month',
    upTo: 'Up to {{amount}}',
    due: 'Due before you move in',
    noFees: 'BC does not allow application, key or admin fees.',
    trust: 'Deposits are held in trust by the brokerage. Rent is paid to the owner, not through relta.ca.',
  },
  fair: {
    title: 'Fair rent check',
    chip: 'Fair rent',
    chipBelow: 'Below nearby rents',
    chipAbove: 'Above nearby rents',
    in: 'In the usual range for asking rents nearby',
    below: 'Below the usual range for asking rents nearby',
    above: 'Above the usual range for asking rents nearby',
    bandLine: 'Similar {{beds}}-bedroom homes near {{place}} are asking {{low}} to {{high}} a month.',
    bandLineStudio: 'Similar studios near {{place}} are asking {{low}} to {{high}} a month.',
    thisHome: 'This home {{rent}}',
    source: 'Range from {{count}} active MLS® asking rents for similar homes within {{km}} km.',
    gateTitle: 'Leased comparables need a free account',
    gateLine: 'See the homes behind this range and what each one leased for.',
    gateCta: 'Create account',
    gateCtaSignedIn: 'Unlock leased rents',
    leasedTitle: '{{count}} leased comparables within {{km}} km',
    leasedBand: 'Leased rents for similar homes ran {{low}} to {{high}} a month.',
    asked: 'Asked',
    leased: 'Leased',
    noLeasedRent: 'Not in the record',
    vowNote: 'Leased rents from MLS® records, shown under VOW terms to registered users. The records carry no leased date.',
    noComparables: 'Not enough leased records nearby to list comparables.',
    loading: 'Checking asking rents nearby',
    error: 'Rents nearby did not load. Try again later.',
    rangeLabel: 'Asking rents nearby run {{low}} to {{high}} a month. This home asks {{rent}}.',
    specBeds: '{{count}} bd',
    specBaths: '{{count}} ba',
    specSqft: '{{value}} sqft',
  },
  path: {
    eyebrow: 'Path to owning',
    title: 'What owning nearby costs',
    yourRent: 'Your rent here',
    toOwn: 'To own a similar {{beds}}-bed',
    toOwnHome: 'To own a similar home',
    median: 'Median asking nearby',
    aMonth: 'a month',
    note: 'Principal and interest at the {{rate}}% posted 5-year rate, 20% down, 25 years. Strata fees and property tax are extra.',
    rowMonthly: '~{{monthly}}/mo at {{rate}}%',
    listedBy: 'Listed by {{office}} · MLS® {{id}}',
    seeForSale: 'See homes for sale in {{place}}',
    rentVsBuy: 'Rent vs buy',
    rentCarries: 'What {{rent}} rent carries',
    railLine: 'Owning nearby: about {{monthly}}/mo',
    railLink: 'See how',
    phoneLine: 'a month to own a similar {{beds}}-bed',
  },
  neighbourhood: {
    title: 'Neighbourhood',
    commuteNote: 'Commute times are not available yet.',
  },
  similar: {
    title: 'Similar rentals nearby',
    alerts: 'Get alerts for similar rentals',
  },
  licensee: {
    name: 'Ray Rasouli, REALTOR®',
    brokerage: 'Real Broker B.C. Ltd.',
    represents: 'Represents the owner',
    listedByLine: 'Listed by Ray Rasouli, REALTOR®, Real Broker B.C. Ltd.',
    aboutTitle: 'About this listing',
    aboutBody: 'Ray Rasouli, REALTOR® with Real Broker B.C. Ltd., lists this home for its owner. Ray represents the owner, not renters. Owner contact details stay private; Ray answers every request.',
    alsoOnMls: 'Also on MLS® as {{id}}.',
    photoAlt: 'Ray Rasouli',
  },
  rayCopy: {
    title: 'Listed by Ray Rasouli for the owner',
    line: 'Your request goes to Ray.',
    link: 'See the full listing',
  },
  states: {
    leasedTitle: 'This rental is leased',
    leasedLine: 'It is no longer available. Similar rentals are below.',
    goneTitle: 'This rental is no longer available',
    goneLine: 'It left the MLS® feed at the last update.',
    ownerEndedLine: 'Ray took this listing down. The page stays for 30 days.',
    pausedTitle: 'This rental is paused',
    pausedLine: 'The owner paused this listing for now.',
    seeSimilar: 'See similar rentals',
    areaAlerts: 'Get alerts for this area',
    browse: 'Browse rentals',
  },
  preview: {
    title: 'Listing preview',
    banner: 'Preview of the working copy. Renters cannot see this page.',
    status: 'Status: {{status}}',
    noPhotos: 'No photos yet',
    missing: 'Not filled in yet',
  },
  viewing: {
    title: 'Request a viewing',
    times: 'Times that work for you',
    anotherTime: 'Another time',
    addTime: 'Add time',
    day: 'Day',
    window: 'Time of day',
    windowValue: {
      morning: 'Morning',
      afternoon: 'Afternoon',
      evening: 'Evening',
    },
    windowLower: {
      morning: 'morning',
      afternoon: 'afternoon',
      evening: 'evening',
    },
    slot: '{{day}} · {{window}}',
    timesMax: 'You can pick up to {{count}} times.',
    timesHelp: 'Pick one or more. The listing agent confirms the exact time.',
    timesHelpOwner: 'Pick one or more. Ray confirms the exact time.',
    moveIn: 'Move-in date',
    note: 'Note (optional)',
    notePlaceholder: 'Questions about the home, parking or pets',
    realtorYes: 'The REALTOR® you work with can also book this viewing for you.',
    optIn: 'Relta Real Estate can follow up later about renting or buying.',
    optInOwner: 'Relta Real Estate can also follow up later about renting or buying.',
    previewCta: 'Preview request',
    previewTitle: 'Check your request',
    previewLead: 'This is exactly what the listing agent gets.',
    previewLeadOwner: 'This is exactly what Ray Rasouli gets.',
    to: 'To: the listing agent at {{brokerage}}',
    toNoBrokerage: 'To: the listing agent for this home',
    toOwner: 'To: Ray Rasouli, REALTOR®',
    subject: 'Subject: Viewing request for {{address}}',
    subjectMls: 'Subject: Viewing request for {{address}} (MLS® {{id}})',
    row: {
      name: 'Name',
      email: 'Email',
      phone: 'Mobile phone',
      times: 'Times',
      moveIn: 'Move-in date',
      realtor: 'Working with a REALTOR®',
      note: 'Note',
    },
    anyTime: 'Any time',
    notGiven: 'Not given',
    keptNote: 'Kept with your account, not sent to the agent: Relta Real Estate can follow up later about renting or buying.',
    edit: 'Edit',
    send: 'Send request',
    sentTitle: 'Request sent',
    sentTitleOwner: 'Request sent to Ray',
    sentLine: 'It went to the listing agent at {{brokerage}}. They reply to you by email or phone to set a time.',
    sentLineNoBrokerage: 'It went to the listing agent for this home. They reply to you by email or phone to set a time.',
    sentLineOwner: 'Ray Rasouli will reply by email or phone to confirm a time.',
    stepsLabel: 'What happens next',
    step1: 'The listing agent reads your times and note.',
    step2: 'They contact you to confirm a viewing.',
    step3: 'A copy is in your email and in Rentals on your dashboard.',
    stepOwner1: 'Ray reads your times and note.',
    stepOwner2: 'Ray contacts you to confirm a viewing.',
    fasterTitle: 'Make the next request faster',
    startProfile: 'Start rental profile',
    leaseReminder: 'Remind me before my lease ends',
    seeSimilar: 'See similar rentals',
    done: 'Done',
    failed: 'That did not go through. Try again.',
    offline: 'You are offline. Your request is kept here. Send it when you are back online.',
    unavailable: 'This rental is no longer available.',
    tooMany: 'Too many requests from this connection. Try again in a few minutes.',
    draftKept: 'Your details are kept on this device until you send.',
  },
} as const;
